import React, { FC, useState, useCallback, useMemo, useEffect } from "react";
import { Box, Text } from "common";
import RadioGroup from "components/RadioGroup";
import { makeStyles, Theme } from "@material-ui/core/styles";
import FileUpload from "components/PromotionForm/CustomFields/FileUpload";
import { useSelector, useDispatch } from "react-redux";
import { IState } from "store/store.interface";
import { ISelectedShipments, IMarketPlaceRadios, Operation } from "store/reducers/shipmentServices.reducers";
import { ParseResult } from "papaparse";
import { setPromotionObject } from "store/actions/promotion.actions";
import {
  generateShipmentRules,
  parseShipmentRules,
} from "./MultiSelectShipmentHelper";
import {
  setSelectedShipments,
  setMarketplaceValues,
} from "store/actions/shipmentServices.actions";

export const useStyles = makeStyles((theme: Theme) => ({
  radioDirection: {
    '& [data-testid="radio-group"]': {
      display: "flex",
      flexDirection: "column !important",
      alignItems: "flex-start",
    },
  },
}));

interface IProps {
  getValue: (keys: string[]) => string | Date;
}

const SellerInformation: FC<IProps> = ({ getValue }) => {

  const dispatch = useDispatch();
  const {
    selectedShipments = [],
    shipmentServices = [],
    marketplaceValues,
    creationForm,
  } = useSelector((state: IState) => ({
    ...state.promotion,
    ...state.shipmentServices,
  }));

  const [error, setError] = useState("");
  const { radioDirection } = useStyles();

  const { retailShipments, marketplaceShipments, b2bShipment } = useMemo(() => {
    const retailShipments: ISelectedShipments[] = [];
    const marketplaceShipments: ISelectedShipments[] = [];
    const b2bShipment: ISelectedShipments[] = [];

    selectedShipments?.forEach((shipment: ISelectedShipments) => {
      if (shipment.sellerType === "RETAIL") {
        retailShipments.push(shipment);
      } else if (shipment.sellerType === "B2B") {
        b2bShipment.push(shipment);
      } else {
        marketplaceShipments.push(shipment);
      }
    });
    return { retailShipments, marketplaceShipments, b2bShipment };
  }, [selectedShipments, creationForm.edit]);

  const defaultValues = useMemo(() => {
    let editValues: IMarketPlaceRadios = {};
    const shipmentRulesValues = getValue(["restrictions", "shipmentRules"]);

    if (
      creationForm.edit &&
      Array.isArray(shipmentRulesValues) &&
      shipmentServices
    ) {
      const { values } = parseShipmentRules(
        shipmentRulesValues,
        shipmentServices
      );
      editValues = values || {};
    }

    return marketplaceShipments.reduce((acc: IMarketPlaceRadios, shipment) => {
      acc[shipment.value] = editValues[shipment.value] || "allSeller";
      return acc;
    }, {});
  }, [marketplaceShipments, creationForm.edit, shipmentServices, getValue]);

  const values = marketplaceValues || {};

  const options = [
    { label: "All Sellers", value: "allSeller" },
    { label: "Include specific sellers", value: "in" },
    { label: "Exclude specific sellers", value: "notIn" },
  ];

  const onChangeRadioHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const value = e.target.value as Operation;
    // remove file data so that no wrong information will go in payload
    dispatch(
      setPromotionObject("", ["restrictions", "shipmentRulesFiles", name])
    );
    dispatch(
      setMarketplaceValues({
        ...marketplaceValues,
        [name]: value,
      })
    );
  };

  // This is used to set allSellers if marketplace Seller selected initially
  useEffect(() => {
    const newEntries: IMarketPlaceRadios = {};

    marketplaceShipments.forEach((shipment) => {
      if (!(shipment.value in values)) {
        newEntries[shipment.value] = "allSeller";
      }
    });

    if (Object.keys(newEntries).length > 0) {
      dispatch(
        setMarketplaceValues({
          ...marketplaceValues,
          ...newEntries,
        })
      );
    }
  }, [marketplaceShipments, marketplaceValues, dispatch]);

  // This is used for setting selected shipmentRules in creationForm
  useEffect(() => {
    if (selectedShipments.length > 0) {
      const updatedRules = generateShipmentRules(
        selectedShipments,
        values,
        getValue
      );
      dispatch(
        setPromotionObject(updatedRules, ["restrictions", "shipmentRules"])
      );
    }
  }, [selectedShipments, values, getValue, dispatch]);

  // This is used to support edit flow as we need values back
  useEffect(() => {
    const shipmentRules = getValue(["restrictions", "shipmentRules"]) as any;
    if (!creationForm.edit || !shipmentServices) return;

    if(shipmentRules.length >0){
       const { selectedShipments, values, shipmentRulesFiles } =
      parseShipmentRules(shipmentRules, shipmentServices);

    dispatch(setMarketplaceValues(values));
    dispatch(setSelectedShipments(selectedShipments));

    if (Object.keys(shipmentRulesFiles).length > 0) {
      dispatch(
        setPromotionObject(shipmentRulesFiles, [
          "restrictions",
          "shipmentRulesFiles",
        ])
      );
    }
    }
  }, [creationForm.edit, dispatch, shipmentServices]);

  useEffect(() => {
    // Initialize values in Redux if empty or on edit
    if (!marketplaceValues || Object.keys(marketplaceValues).length === 0) {
      dispatch(setMarketplaceValues(defaultValues));
    }
  }, [dispatch, defaultValues, marketplaceValues]);

  const onCompleteFileUpload = useCallback(
    (name: string) => (result: ParseResult<any>) => {
      const { data } = result;
      let finalSkus = ([] as string[]).concat(...data);
      dispatch(
        setPromotionObject(finalSkus, [
          "restrictions",
          "shipmentRulesFiles",
          name,
        ])
      );
    },
    [dispatch]
  );

  if(selectedShipments.length === 0){
    return null;
  }

  return (
    <Box>
      <Text fontSize="18px" fontWeight="500" mb={1}>
        Seller Information
      </Text>
      {retailShipments.length > 0 && (
        <Box mb={2}>
          <Text fontSize="14px">
            {retailShipments
              .map((shipment: ISelectedShipments) => shipment.label)
              .join(", ")}
          </Text>
          <RadioGroup
            testId="retail-seller-radio"
            value="retail"
            options={[{ label: "Retail", value: "retail" }]}
          />
        </Box>
      )}
      {b2bShipment.length > 0 && (
        <Box mb={2}>
          <Text fontSize="14px">{b2bShipment[0].label}</Text>
          <RadioGroup
            testId="b2b-seller-radio"
            value="B2B"
            options={[{ label: "B2B", value: "B2B" }]}
          />
        </Box>
      )}
      {marketplaceShipments.map((shipment) => (
        <Box key={shipment.value} display="flex" flexDirection="column" mb={2}>
          <Text fontSize="14px">{shipment.label}</Text>
          <Box className={radioDirection}>
            <RadioGroup
              testId="marketplace-radio-group"
              name={shipment.label}
              value={values[shipment.value] || ""}
              onChange={(e) => onChangeRadioHandler(e, shipment.value)}
              options={options}
            />
          </Box>
          {(values[shipment.value] === "in" ||
            values[shipment.value] === "notIn") && (
            <FileUpload
              key={values[shipment.value]}
              onCompleteUpload={onCompleteFileUpload(shipment.value)}
              setError={setError}
              buttonTitle="Upload"
              label="Choose a CSV file"
              fileSelected={
                !!getValue(["restrictions", "shipmentRulesFiles", shipment.value])
              }
              error={error}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default SellerInformation;
